import './UpdatesFeed.css';
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useMemo, useReducer, useRef } from 'preact/hooks';
import { focusIfKeyboard } from '../../a11y/focus';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { CardHeader } from '../../components/CardHeader';
import { FlipList } from '../../components/FlipList';
import { ScrollArea } from '../../components/ScrollArea';
import { eventRich } from '../../copy/events';
import { t } from '../../copy/strings';
import { T_OPEN } from '../../model/time';
import type { ElectionEvent } from '../../model/types';
import { useConfig, useNow, useStatics, useTimeline, useView } from '../../state/hooks';
import { FEED_PAGE, feedEvents } from './model';
import { UpdateItem } from './UpdateItem';

/** A noite em destaque na vitrine do replay antes de 25/10: o 2º turno de 2022 (a chave do catálogo do acervo). */
export const REPLAY_SHOWCASE_NIGHT = '2022-2t';

export interface UpdatesFeedProps {
  /**
   * `EventList.truncated` do último `events.json` (§7.15 "Fim do histórico disponível"). O FeedClient ainda não expõe o
   * campo; até lá, o integrador passa `false` (padrão) e a nota não aparece.
   */
  truncated?: boolean;
}

/** id do título (§7.15 bloco 2); o `Card` aponta `aria-labelledby` para ele e ele é o alvo do foco de reserva. */
export const UPDATES_TITLE_ID = 'updates-title';
/** Máscara de continuação do feed (§3.9, §7.15): 48 px. */
const FADE_PX = 48;

interface Anchor {
  key: string;
  top: number;
}

/** O 1º item visível da área rolável e a distância dele ao topo da área (compensação de `scrollTop`, §7.15 bloco 5). */
function measureAnchor(scroller: HTMLElement): Anchor | null {
  const top = scroller.getBoundingClientRect().top;
  for (const li of scroller.querySelectorAll<HTMLElement>('.updates-feed__item')) {
    const r = li.getBoundingClientRect();
    if (r.bottom > top + 0.5 && li.dataset.key !== undefined) return { key: li.dataset.key, top: r.top - top };
  }
  return null;
}

/**
 * "Últimas atualizações" (§7.15): eventos da noite em ordem cronológica inversa, 16 de cada vez com "Ver mais" e
 * rolagem real (M22), chaves estáveis pelo id do evento (`FlipList`), sem `aria-live` (§15.3: só a `LiveRegion`
 * anuncia). Regras de foco de §15.2.2: chegada de evento nunca move o foco; item focado continua focado; item focado
 * que sai da lista devolve o foco ao título; "Ver mais" leva o foco ao 1º item novo. Com a lista rolada para baixo, a
 * entrada de itens no topo não move o que está à vista (AC-7.15-10).
 */
export function UpdatesFeed({ truncated = false }: UpdatesFeedProps) {
  const view = useView();
  const cfg = useConfig();
  const { live, displayTMin } = useTimeline();
  const geo = useStatics().geography;
  const probabilityOn = cfg.features.probability;
  const targets = { abroad: cfg.features.abroad, governors: cfg.features.governors };
  const electionDate = cfg.election_date;

  const all = useMemo<ElectionEvent[]>(
    () => (view ? feedEvents(view.events, { probabilityOn, electionDate }) : []),
    [view, probabilityOn, electionDate],
  );
  // Evento sem texto montável (nome ou lugar ausente no dado) não entra: nunca um item vazio. A conferência escreve o
  // texto, e por isso é feita só até completar a página (mais um, para o "Ver mais"), e guardada por evento: o nome e o
  // lugar de um evento não mudam com o seq; a geografia nova refaz tudo (TBT do boot, DECISOES §49).
  const textable = useRef<{ geo: unknown; ok: Map<string, boolean> }>({ geo, ok: new Map() });
  if (textable.current.geo !== geo) textable.current = { geo, ok: new Map() };
  const hasText = (ev: ElectionEvent): boolean => {
    const cache = textable.current.ok;
    let ok = cache.get(ev.id);
    if (ok === undefined && view) {
      ok = eventRich(ev, view, 'line', geo) !== null;
      cache.set(ev.id, ok);
    }
    return ok === true;
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const count = useRef(FEED_PAGE);
  const prevAll = useRef<ElectionEvent[] | null>(null);
  const anchor = useRef<Anchor | null>(null);
  const pendingFocus = useRef<{ index: number; hadFocus: boolean } | null>(null);
  const [, rerender] = useReducer((x: number) => x + 1, 0);

  // Antes do diff (o DOM ainda é o da lista anterior): rolagem e foco atuais.
  const root = rootRef.current;
  const scroller = root?.querySelector<HTMLElement>('.scroll-area') ?? null;
  const scrolled = scroller !== null && scroller.scrollTop > 0;
  const prev = prevAll.current;
  if (prev !== null && prev !== all) {
    if (scrolled && scroller) anchor.current = measureAnchor(scroller);
    // Ao vivo, com a pessoa na lista (rolada ou com o foco dentro), os itens novos do topo somam à janela: nada do que
    // ela está lendo ou focou sai pelo fim (§15.2.2 regra 4).
    const active = typeof document === 'undefined' ? null : document.activeElement;
    const engaged = scrolled || (root !== null && active !== null && root.contains(active));
    const first = prev[0];
    if (live && engaged && first) {
      const before = new Set(prev.map((e) => e.id));
      let added = 0;
      for (const ev of all) {
        if (ev.id === first.id) break;
        if (!before.has(ev.id)) added++;
      }
      count.current += added;
    }
  }
  prevAll.current = all;

  const shown: ElectionEvent[] = [];
  let hasMore = false;
  for (const ev of all) {
    if (!hasText(ev)) continue;
    if (shown.length === count.current) {
      hasMore = true;
      break;
    }
    shown.push(ev);
  }

  // O título é o alvo de reserva do foco (§15.2.2 regra 4): precisa de tabindex="-1".
  useLayoutEffect(() => {
    const h = rootRef.current?.querySelector<HTMLElement>(`#${UPDATES_TITLE_ID}`) ?? null;
    if (h && h.getAttribute('tabindex') !== '-1') h.setAttribute('tabindex', '-1');
    titleRef.current = h;
  });

  // Compensação da rolagem: o item que estava no topo da área volta à mesma distância (AC-7.15-10, ±2 px).
  useLayoutEffect(() => {
    const a = anchor.current;
    anchor.current = null;
    const el = rootRef.current?.querySelector<HTMLElement>('.scroll-area');
    if (!a || !el) return;
    const li = Array.from(el.querySelectorAll<HTMLElement>('.updates-feed__item')).find((x) => x.dataset.key === a.key);
    if (!li) return;
    const delta = li.getBoundingClientRect().top - el.getBoundingClientRect().top - a.top;
    if (Math.abs(delta) >= 0.5) el.scrollTop += delta;
  });

  // "Ver mais" (§7.15 bloco 5, §15.2.2 regra 6): o foco vai ao 1º item acrescentado (todo item é focável: botão ou
  // bloco com tabindex="-1"); sem item novo, ao último. Com o ponteiro, o foco só sai do botão quando ele some.
  useLayoutEffect(() => {
    const pf = pendingFocus.current;
    if (!pf) return;
    pendingFocus.current = null;
    const items = rootRef.current?.querySelectorAll<HTMLElement>('.updates-feed__item');
    if (!items || items.length === 0) return;
    const li = items[pf.index] ?? items[items.length - 1];
    const el = li?.querySelector<HTMLElement>('.update-item') ?? null;
    if (!el) return;
    if (focusIfKeyboard(el)) return;
    if (pf.hadFocus && moreRef.current === null) el.focus({ preventScroll: true });
  });

  const onMore = () => {
    const from = shown.length;
    pendingFocus.current = { index: from, hadFocus: document.activeElement === moreRef.current };
    count.current = Math.max(count.current, from) + FEED_PAGE;
    rerender(0);
  };

  let body: ComponentChildren;
  if (shown.length === 0) {
    // Instante exibido (§7.15 bloco 4): ao vivo, o relógio da fonte (as 17h00 chegam pelo tique, sem esperar snapshot);
    // no replay, o instante pedido. O tique só é assinado com a lista vazia.
    const nowTMin = useNow().tMin;
    const instant = live ? Math.max(nowTMin, displayTMin) : displayTMin;
    // Vitrine do replay (§8 de 16/10, 12h20, item 2; ORD-F-014 item 2 c): até o dia da eleição (t_min < 0 é antes de
    // 25/10, 00h00 de Brasília), o painel vazio leva à noite do 2º turno de 2022 no site do replay.
    const replayUrl = import.meta.env.VITE_REPLAY_URL;
    const showReplay = live && Boolean(replayUrl) && nowTMin < 0;
    body = (
      <>
        <p class="updates-feed__empty" data-testid="updates-empty">
          {t(instant < T_OPEN ? 'updates.empty.beforeOpen' : 'updates.empty.afterOpen')}
        </p>
        {showReplay ? (
          <a
            class="updates-feed__replay"
            href={`${replayUrl.replace(/\/$/, '')}/?noite=${REPLAY_SHOWCASE_NIGHT}`}
            data-testid="updates-replay"
          >
            {t('updates.replay')}
          </a>
        ) : null}
      </>
    );
  } else {
    const v = view as NonNullable<typeof view>;
    body = (
      <>
        <FlipList
          as="ol"
          items={shown.map((ev) => ({ key: ev.id, ev }))}
          renderItem={(it) => (
            <UpdateItem event={it.ev} view={v} geo={geo} photos={cfg.features.photos} targets={targets} />
          )}
          replay={!live || scrolled}
          focusFallback={titleRef}
          class="updates-feed__list"
          itemClass="updates-feed__item"
        />
        {hasMore ? (
          <Button
            label={t('updates.more')}
            variant="ghost"
            class="updates-feed__more"
            buttonRef={moreRef}
            onClick={onMore}
          />
        ) : truncated ? (
          <p class="updates-feed__note" data-testid="updates-truncated">
            {t('updates.truncated')}
          </p>
        ) : null}
      </>
    );
  }

  return (
    <Card
      variant="shrink"
      class="updates-feed"
      data-testid="updates-feed"
      data-state={shown.length === 0 ? 'empty' : 'list'}
      labelledBy={UPDATES_TITLE_ID}
    >
      <div class="updates-feed__inner" ref={rootRef}>
        {/* h2: título de cartão de topo (§15.4.1, dona da estrutura de títulos; §7.15 bloco 2 diz h3), como "Por região" */}
        <CardHeader title={t('updates.title')} titleId={UPDATES_TITLE_ID} />
        <ScrollArea label={t('updates.region.aria')} fade={FADE_PX} region="always" class="updates-feed__scroll">
          {body}
        </ScrollArea>
      </div>
    </Card>
  );
}
